/**
 * CheckoutsPage
 *
 * Lists all checkouts (kiosks) for the currently-selected organization,
 * fetched via GET /api/orgs/{orgId}/checkouts and refreshed every 5 seconds
 * while this page is open so kiosk heartbeat status changes appear promptly.
 *
 * UI redesign (May 2026 Figma):
 *   - Page title "Checkout management" with inline status summary counts
 *     (TOTAL / ACTIVE / INACTIVE / ERRORS / PENDING).
 *   - Search is an icon button that toggles an inline input.
 *   - Settings gear (placeholder) sits next to "+ Add checkout".
 *   - Table simplified: checkbox / LOCATION / # OF DEVICES / STATUS /
 *     LAST SYNCED / CHECKOUT TEMPLATE / [Launch] [kebab].
 *   - Status pills support 4 states: Active / Pending / Inactive / Error.
 *     Backend ONLINE/OFFLINE values are normalized into ACTIVE/INACTIVE.
 *   - Edit/Delete moved into the kebab menu.
 *
 * Tenant context for downstream kiosk pages:
 *   - Org   -> from the global header picker (currentOrg)
 *   - Site  -> from the row itself.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import CheckoutDrawer from '../components/checkouts/CheckoutDrawer';
import checkoutService from '../services/checkoutService';
import checkoutTemplateService from '../services/checkoutTemplateService';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/checkouts-page`;
const CHECKOUT_REFRESH_INTERVAL_MS = 5_000;

// Legacy static map kept as a fallback for orgs that haven't run the migration yet.
const TEMPLATE_LABELS_FALLBACK = {
  STANDARD:      'Org Default',
  OPEN_ACCESS:   'Open Access Checkout',
  HIGH_SECURITY: 'High Security Checkout',
};

/** Normalize backend status into one of: ACTIVE | INACTIVE | ERROR | PENDING. */
function normalizeStatus(raw) {
  const s = String(raw ?? '').toUpperCase();
  if (s === 'ONLINE'  || s === 'ACTIVE')   return 'ACTIVE';
  if (s === 'OFFLINE' || s === 'INACTIVE') return 'INACTIVE';
  if (s === 'ERROR'   || s === 'FAILED')   return 'ERROR';
  if (s === 'PENDING' || s === 'SYNCING')  return 'PENDING';
  return s || 'INACTIVE';
}

function formatLastSync(iso) {
  if (!iso) return '\u2014';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return String(iso);
  }
}

function mapRow(dto, templateNameMap = {}) {
  const templateName =
    templateNameMap[dto.template]
    ?? TEMPLATE_LABELS_FALLBACK[dto.template]
    ?? dto.template
    ?? 'Org Default';
  return {
    id:          dto.checkoutId,
    name:        dto.checkoutName,
    siteId:      dto.siteId,
    site:        dto.siteName,
    location:    dto.location || '',
    status:      normalizeStatus(dto.status),
    statusRaw:   dto.status,
    template:    templateName,
    templateRaw: dto.template,
    devices:     dto.dockCount ?? 0,
    lastSync:    formatLastSync(dto.lastSyncAt),
  };
}

const COLUMNS = [
  { id: 'name',     label: 'CHECKOUT NAME' },
  { id: 'location', label: 'LOCATION' },
  { id: 'devices',  label: '# OF DEVICES' },
  { id: 'status',   label: 'STATUS' },
  { id: 'lastSync', label: 'LAST SYNCED' },
  { id: 'template', label: 'CHECKOUT TEMPLATE' },
];

const STATUS_META = {
  ACTIVE:   { label: 'Active',   className: 'cm-status-active',   img: `${ICON_BASE}/active.svg` },
  PENDING:  { label: 'Pending',  className: 'cm-status-pending',  img: `${ICON_BASE}/pending.svg` },
  INACTIVE: { label: 'Inactive', className: 'cm-status-inactive', img: `${ICON_BASE}/inactive.svg` },
  ERROR:    { label: 'Error',    className: 'cm-status-error',    img: `${ICON_BASE}/errors.svg` },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.INACTIVE;
  return (
    <span className={`cm-status ${meta.className}`}>
      <img src={meta.img} alt="" className="cm-status-img" />
      {meta.label}
    </span>
  );
}

/** Lightweight kebab popover; closes on outside-click / Esc. */
function RowKebabMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);
  return (
    <div className="cm-kebab" ref={ref}>
      <button
        type="button"
        className="cm-kebab-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
        onClick={() => setOpen((v) => !v)}
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>
      {open && (
        <div className="cm-kebab-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onEdit(); }}>
            <i className="fas fa-pencil-alt"></i> Edit
          </button>
          <button type="button" role="menuitem" className="cm-kebab-danger" onClick={() => { setOpen(false); onDelete(); }}>
            <i className="fas fa-trash"></i> Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function CheckoutsPage() {
  const { orgId, currentOrg, sites } = useTenant();
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [loadError, setLoadError]   = useState(null);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort]             = useState({ key: 'name', asc: true });
  const [selected, setSelected]     = useState(() => new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState(null);
  // code → human name resolved from the templates API
  const [templateNameMap, setTemplateNameMap] = useState({});

  // Fetch template names once per orgId so the table shows real names.
  useEffect(() => {
    if (!orgId) return;
    checkoutTemplateService
      .listTemplates(orgId, { limit: 200, offset: 0 })
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.content ?? []);
        const map = {};
        list.forEach((t) => { map[t.templateCode] = t.templateName; });
        setTemplateNameMap(map);
      })
      .catch(() => setTemplateNameMap({}));
  }, [orgId]);

  const loadCheckouts = useCallback(async (silent = false) => {
    if (!orgId) return;
    if (!silent) {
      setLoading(true);
      setLoadError(null);
    }
    try {
      // Checkout management intentionally shows all checkouts for the org.
      const data = await checkoutService.listCheckouts(orgId, { limit: 200, offset: 0 });
      const list = Array.isArray(data) ? data : (data?.content ?? data?.items ?? []);
      setRows((Array.isArray(list) ? list : []).map((dto) => mapRow(dto, templateNameMap)));
    } catch (err) {
      if (!silent) {
        setLoadError(
          err?.response?.data?.message
          ?? err?.response?.data?.error
          ?? err.message
          ?? 'Failed to load checkouts',
        );
        setRows([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orgId, templateNameMap]);
  useEffect(() => {
    loadCheckouts();
    const intervalId = window.setInterval(
      () => loadCheckouts(true),
      CHECKOUT_REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(intervalId);
  }, [loadCheckouts]);

  /** Summary counts derived from current rows. */
  const summary = useMemo(() => {
    const acc = { total: rows.length, ACTIVE: 0, INACTIVE: 0, ERROR: 0, PENDING: 0 };
    rows.forEach((r) => { acc[r.status] = (acc[r.status] ?? 0) + 1; });
    return acc;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = !q
      ? rows
      : rows.filter((r) =>
          [r.name, r.site, r.location, r.status, r.template].some((v) =>
            String(v ?? '').toLowerCase().includes(q),
          ),
        );
    const sorted = [...data].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (sort.asc ? 1 : -1) * (av - bv);
      return (sort.asc ? 1 : -1) * String(av ?? '').localeCompare(String(bv ?? ''));
    });
    return sorted;
  }, [rows, search, sort]);

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, asc: !prev.asc } : { key, asc: true }));

  const allChecked  = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (row) => {
    if (!orgId) return;
    if (!window.confirm(`Delete checkout "${row.name}"?`)) return;
    try {
      await checkoutService.deleteCheckout(orgId, row.id);
      await loadCheckouts();
    } catch (err) {
      alert(
        err?.response?.data?.message
        ?? err?.response?.data?.error
        ?? 'Failed to delete checkout',
      );
    }
  };

  const handleLaunch = (row) => {
    const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
    const orgName        = currentOrg?.name;
    const orgIdForLaunch = currentOrg?.orgId ?? currentOrg?.id;
    const siteName       = row.site;
    const matchedSite    = (sites || []).find((s) => s.name === siteName);
    const siteId         = row.siteId ?? matchedSite?.siteId ?? matchedSite?.id;
    if (!orgIdForLaunch) {
      window.alert('Please select an organization from the header before launching a checkout.');
      return;
    }
    if (!siteId) {
      window.alert('This checkout has no site assigned. Edit the checkout and assign a site first.');
      return;
    }
    const qp = new URLSearchParams({ checkoutId: String(row.id) });
    if (orgName)                qp.set('org',    orgName);
    if (orgIdForLaunch != null) qp.set('orgId',  String(orgIdForLaunch));
    if (siteName)               qp.set('site',   siteName);
    if (siteId != null)         qp.set('siteId', String(siteId));
    window.open(`${base}/checkout/camera?${qp.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* ---- Header: search icon, title, summary, settings + add ---- */}
      <div className="cm-toolbar">
        <div className="cm-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search checkouts"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <img src={`${ICON_BASE}/search.svg`} alt="" className="cm-icon-img" />
          </button>
          <div className="cm-toolbar-heading">
            <h1>Checkout management</h1>
            <div className="cm-summary">
              <span className="cm-summary-item"><strong>{summary.total}</strong> TOTAL</span>
              <span className="cm-summary-item"><img src={`${ICON_BASE}/active.svg`}   alt="" className="cm-summary-img" /><strong>{summary.ACTIVE}</strong> ACTIVE</span>
              <span className="cm-summary-item"><img src={`${ICON_BASE}/inactive.svg`} alt="" className="cm-summary-img" /><strong>{summary.INACTIVE}</strong> INACTIVE</span>
              <span className="cm-summary-item"><img src={`${ICON_BASE}/errors.svg`}   alt="" className="cm-summary-img" /><strong>{summary.ERROR}</strong> ERRORS</span>
              <span className="cm-summary-item"><img src={`${ICON_BASE}/pending.svg`}  alt="" className="cm-summary-img" /><strong>{summary.PENDING}</strong> PENDING</span>
            </div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search checkouts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="cm-toolbar-right">
          <button
            className="btn-primary-pill"
            onClick={() => { setEditing(null); setDrawerOpen(true); }}
          >
            <i className="fas fa-plus"></i> Add checkout
          </button>
        </div>
      </div>

      {loadError && (
        <div className="users-error" style={{ marginBottom: 14 }}>{loadError}</div>
      )}

      <table className="data-table cm-table">
        <thead>
          <tr>
            <th className="cm-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                onChange={toggleAll}
                aria-label="Select all"
              />
            </th>
            {COLUMNS.map((c) => (
              <th key={c.id}>
                <button className="data-table-sort" onClick={() => toggleSort(c.id)}>
                  {c.label}
                  <i className={`fas fa-sort${
                    sort.key === c.id ? (sort.asc ? '-up' : '-down') : ''
                  }`}></i>
                </button>
              </th>
            ))}
            <th className="cm-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-muted)' }}>
                Loading...
              </td>
            </tr>
          )}
          {!loading && filtered.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length + 2} style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-muted)' }}>
                No checkouts yet. Click <strong>+ Add checkout</strong> to create one.
              </td>
            </tr>
          )}
          {filtered.map((r) => (
            <tr key={r.id}>
              <td className="cm-col-check">
                <input
                  type="checkbox"
                  checked={selected.has(r.id)}
                  onChange={() => toggleRow(r.id)}
                  aria-label={`Select ${r.location}`}
                />
              </td>
              <td style={{ fontWeight: 500 }}>{r.name}</td>
              <td>{r.location}</td>
              <td>{r.devices}</td>
              <td><StatusPill status={r.status} /></td>
              <td>{r.lastSync}</td>
              <td>{r.template}</td>
              <td className="cm-col-actions">
                <button
                  type="button"
                  className="cm-launch-btn"
                  title="Launch checkout"
                  onClick={() => handleLaunch(r)}
                >
                  <img src={`${ICON_BASE}/signin.svg`} alt="" className="cm-launch-img" />
                  Launch
                </button>
                <RowKebabMenu
                  onEdit={() => { setEditing(r); setDrawerOpen(true); }}
                  onDelete={() => handleDelete(r)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <CheckoutDrawer
        open={drawerOpen}
        checkout={editing}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        onSaved={async () => {
          setDrawerOpen(false);
          setEditing(null);
          await loadCheckouts();
        }}
      />
    </>
  );
}
